(() => {
  const SELECTOR = '[data-dpp-build-header]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 767px)');
  const MOBILE_FIT_SAFETY = 8;

  const prepareTitle = (title) => {
    if (title.dataset.buildHeaderLettersReady === 'true') return;

    const label = title.textContent.trim();
    title.dataset.buildHeaderLettersReady = 'true';
    title.setAttribute('aria-label', label);
    title.textContent = '';

    Array.from(label).forEach((character, index) => {
      const letter = document.createElement('span');
      letter.className = 'dpp-mobile-build-header__letter';
      letter.setAttribute('aria-hidden', 'true');
      letter.style.setProperty('--dpp-build-header-letter-index', index);
      letter.textContent = character === ' ' ? '\u00a0' : character;
      title.appendChild(letter);
    });
  };

  const getAvailableWidth = (title) => {
    const identity = title.closest('.dpp-mobile-build-header__identity');
    const content = title.closest('.dpp-mobile-build-header__content');
    const container = identity?.classList.contains('dpp-mobile-build-header__identity--image')
      ? identity
      : content;

    if (!container) return 0;

    const styles = getComputedStyle(container);
    return Math.max(
      0,
      container.clientWidth
        - parseFloat(styles.paddingLeft || 0)
        - parseFloat(styles.paddingRight || 0),
    );
  };

  const fitTitle = (header, title) => {
    if (title.offsetParent === null) return;

    const maximum = Number(
      MOBILE_QUERY.matches
        ? header.dataset.titleMaxMobile
        : header.dataset.titleMaxDesktop,
    ) || 82;
    const availableWidth = Math.max(
      0,
      getAvailableWidth(title) - (MOBILE_QUERY.matches ? MOBILE_FIT_SAFETY : 0),
    );
    if (availableWidth <= 0) return;

    if (MOBILE_QUERY.matches) {
      title.style.removeProperty('--dpp-build-header-title-min-width');
    } else {
      title.style.setProperty('--dpp-build-header-title-min-width', `${availableWidth * 0.5}px`);
    }

    const getIntrinsicTitleWidth = () => Array.from(title.children).reduce(
      (width, letter) => width + letter.scrollWidth,
      0,
    );

    title.style.setProperty('font-size', `${maximum}px`);
    if (getIntrinsicTitleWidth() <= availableWidth) return;

    let low = 12;
    let high = maximum;
    let fitted = low;

    while (low <= high) {
      const candidate = Math.floor((low + high) / 2);
      title.style.setProperty('font-size', `${candidate}px`);

      if (getIntrinsicTitleWidth() <= availableWidth) {
        fitted = candidate;
        low = candidate + 1;
      } else {
        high = candidate - 1;
      }
    }

    title.style.setProperty('font-size', `${fitted}px`);
  };

  const fitTitles = (header) => {
    header.querySelectorAll('[data-build-header-title]').forEach((title) => fitTitle(header, title));
  };

  const scheduleFit = (header) => {
    if (header.buildHeaderFitFrame) cancelAnimationFrame(header.buildHeaderFitFrame);
    header.buildHeaderFitFrame = requestAnimationFrame(() => {
      header.buildHeaderFitFrame = 0;
      fitTitles(header);
    });
  };

  const refitAll = () => {
    document.querySelectorAll(SELECTOR).forEach(scheduleFit);
  };

  const scheduleInitialRefits = () => {
    [80, 240, 600, 1200].forEach((delay) => window.setTimeout(refitAll, delay));
  };

  const initialize = (scope = document) => {
    const headers = [];
    if (scope.matches?.(SELECTOR)) headers.push(scope);
    scope.querySelectorAll?.(SELECTOR).forEach((header) => headers.push(header));

    headers.forEach((header) => {
      if (header.dataset.buildHeaderReady === 'true') return;
      header.dataset.buildHeaderReady = 'true';
      header.querySelectorAll('[data-build-header-title]').forEach(prepareTitle);
      header.querySelectorAll('[data-build-header-sequence]').forEach((element, index) => {
        element.style.setProperty('--dpp-build-header-sequence-index', index);
      });

      fitTitles(header);

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => scheduleFit(header));
        observer.observe(header);
        header.buildHeaderFitObserver = observer;
      }

      requestAnimationFrame(() => requestAnimationFrame(() => {
        fitTitles(header);
        header.classList.add('is-ready');
      }));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('load', () => {
    refitAll();
    scheduleInitialRefits();
  }, { once: true });
  window.addEventListener('pageshow', refitAll);
  window.visualViewport?.addEventListener('resize', refitAll);
  MOBILE_QUERY.addEventListener?.('change', refitAll);
  document.fonts?.addEventListener?.('loadingdone', refitAll);
  document.fonts?.ready.then(refitAll);
  scheduleInitialRefits();
})();
